// =====================================================================
// tpCustomer — app entry
//
// Wires the auth provider → data provider → root component. Loaded by
// index.html as the last script; depends on every preceding dist/*.js
// having attached its exports to window already.
// =====================================================================

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "dark",
  "persona": "GTM",
  "density": "comfortable",
  "__panelOpen": false
}/*EDITMODE-END*/;

function Root() {
  const [route, navigate] = useHashRoute();
  const [tweaks, setTweaksRaw] = useState(TWEAK_DEFAULTS);

  const setTweak = useCallback((keyOrObj, val) => {
    const edits = typeof keyOrObj === "object" ? keyOrObj : { [keyOrObj]: val };
    setTweaksRaw(prev => ({ ...prev, ...edits }));
    try { window.parent.postMessage({ type: "__edit_mode_set_keys", edits }, "*"); } catch (e) {}
  }, []);

  // Edit-mode integration (for the toolbar Tweaks button)
  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type === "__activate_edit_mode") setTweak("__panelOpen", true);
      if (d.type === "__deactivate_edit_mode") setTweak("__panelOpen", false);
    };
    window.addEventListener("message", onMsg);
    try { window.parent.postMessage({ type: "__edit_mode_available" }, "*"); } catch (e) {}
    return () => window.removeEventListener("message", onMsg);
  }, [setTweak]);

  // Apply theme to root
  useEffect(() => {
    document.documentElement.dataset.theme = tweaks.theme || "light";
    document.documentElement.dataset.density = tweaks.density || "comfortable";
  }, [tweaks.theme, tweaks.density]);

  const personaCtx = useMemo(() => ({
    persona: tweaks.persona || "GTM",
    setPersona: (p) => setTweak("persona", p),
  }), [tweaks.persona, setTweak]);

  const tweaksCtx = useMemo(() => ({ tweaks, setTweak }), [tweaks, setTweak]);

  // ---- route resolution ------------------------------------------
  const parts = route.split("/").filter(Boolean);
  let page = null;
  const [section, sub, id] = parts;

  if (section === "sales") {
    if (sub === "dashboard" || !sub) page = <SalesDashboard navigate={navigate} />;
    else if (sub === "leads")          page = id ? <LeadDetail id={id} navigate={navigate} /> : <LeadsList navigate={navigate} />;
    else if (sub === "accounts")       page = id ? <AccountDetail id={id} navigate={navigate} /> : <AccountsList navigate={navigate} />;
    else if (sub === "contacts") {
      const [, , , childRoute] = parts;
      if (id && childRoute === "meddpicc") page = <MeddpiccPage id={id} navigate={navigate} />;
      else if (id)                          page = <ContactDetail id={id} navigate={navigate} />;
      else                                  page = <ContactsList navigate={navigate} />;
    }
    else if (sub === "opportunities") {
      if (id === "list")   page = <OppList navigate={navigate} />;
      else if (id)         page = <OppDetail id={id} navigate={navigate} />;
      else                 page = <OppKanban navigate={navigate} />;
    }
    else if (sub === "outreach")       page = <OutreachPage navigate={navigate} />;
    // Per-sequence detail still lives under /sales/sequences/:id so the
    // existing list-row links and back-out flow keep working.
    else if (sub === "sequences")      page = id ? <SequenceDetail id={id} navigate={navigate} /> : <OutreachPage navigate={navigate} initialTab="sequences" />;
    else if (sub === "calendar")       page = <CalendarPage navigate={navigate} />;
    else if (sub === "activity")       page = <ActivityPage navigate={navigate} />;
    // Legacy routes kept so existing bookmarks/links still resolve.
    else if (sub === "follow-ups")     page = <ActivityPage navigate={navigate} initialTab="upcoming" />;
    else if (sub === "activities")     page = <ActivityPage navigate={navigate} initialTab="history" />;
    else if (sub === "quotes")         page = <QuotesPage navigate={navigate} />;
    else if (sub === "campaigns")      page = <OutreachPage navigate={navigate} initialTab="campaigns" />;
    else if (sub === "referrals")      page = <ReferralsPage navigate={navigate} />;
    else if (sub === "partners")       page = <PartnersList navigate={navigate} />;
    else if (sub === "forecast")       page = <ForecastPage navigate={navigate} />;
    else if (sub === "churn")          page = <ChurnPage navigate={navigate} />;
    // /sales/pilots/<tab> — third URL segment is the initial tab key
    // (metrics | pilots | gatelog). Unknown values fall back to metrics
    // via the validTab() guard inside PilotsPage.
    else if (sub === "pilots")         page = <PilotsPage navigate={navigate} initialTab={id} />;
    else if (sub === "library")        page = <LibraryPage navigate={navigate} />;
  } else if (section === "research") {
    if (sub === "dashboard")           page = <ResearchDashboard navigate={navigate} />;
    else if (sub === "surveys")        page = id ? <SurveyDetail id={id} navigate={navigate} /> : <SurveysList navigate={navigate} />;
    else if (sub === "interviews")     page = id ? <InterviewDetail id={id} navigate={navigate} /> : <InterviewsList navigate={navigate} />;
    else if (sub === "champions")      page = <ChampionsPage navigate={navigate} />;
    else if (sub === "influencers")    page = <InfluencersPage navigate={navigate} />;
    else if (sub === "icp")            page = <IcpPage navigate={navigate} />;
    else if (sub === "insights")       page = id ? <InsightDetail id={id} navigate={navigate} /> : <InsightsPage navigate={navigate} />;
  } else if (section === "admin") {
    page = <AdminPage />;
  }
  if (!page) page = <SalesDashboard navigate={navigate} />;

  return (
    <PersonaContext.Provider value={personaCtx}>
      <TweaksContext.Provider value={tweaksCtx}>
        <Shell route={route} navigate={navigate}>{page}</Shell>
        {tweaks.__panelOpen && <TpCrmTweaksPanel tweaks={tweaks} setTweak={setTweak} />}
      </TweaksContext.Provider>
    </PersonaContext.Provider>
  );
}

// RealAuthProvider gates the whole app: if there's no valid session it
// renders the login screen; once signed in it renders DataProvider →
// Root. DataProvider must be inside the auth provider so its fetches
// pick up the auth cookie that login set.
ReactDOM.createRoot(document.getElementById("root")).render(
  <RealAuthProvider>
    <DataProvider>
      <Root />
    </DataProvider>
  </RealAuthProvider>
);
